import { useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';

interface UseIdleTimeoutOptions {
  timeoutMs: number;
  onTimeout: () => void;
  warningMs?: number;
  onWarning?: () => void;
}

/**
 * Hook to detect user inactivity and trigger timeout actions
 * @param timeoutMs - Time in milliseconds before timeout (e.g., 5 * 60 * 1000 for 5 minutes)
 * @param onTimeout - Callback function to execute when timeout occurs
 * @param warningMs - Optional time in milliseconds before timeout to show warning
 * @param onWarning - Optional callback function to execute when warning threshold is reached
 */
const useIdleTimeout = ({
  timeoutMs,
  onTimeout,
  warningMs,
  onWarning,
}: UseIdleTimeoutOptions) => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearTimers = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    if (warningTimeoutIdRef.current) {
      clearTimeout(warningTimeoutIdRef.current);
      warningTimeoutIdRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    clearTimers();
    lastActivityRef.current = Date.now();

    // Set warning timer if configured
    if (warningMs && onWarning) {
      warningTimeoutIdRef.current = setTimeout(() => {
        logger.warn('Idle timeout warning triggered');
        onWarning();
      }, warningMs);
    }

    // Set timeout timer
    timeoutIdRef.current = setTimeout(() => {
      const idleDuration = Date.now() - lastActivityRef.current;
      logger.info(`User idle timeout triggered after ${Math.floor(idleDuration / 1000)} seconds`);
      onTimeout();
    }, timeoutMs);
  }, [timeoutMs, onTimeout, warningMs, onWarning, clearTimers]);

  useEffect(() => {
    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle the reset to avoid excessive calls
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledResetTimer = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetTimer();
          throttleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, throttledResetTimer, { passive: true });
    });

    // Initialize timer
    resetTimer();

    logger.info(`Idle timeout initialized: ${timeoutMs / 1000} seconds`);

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, throttledResetTimer);
      });
      clearTimers();
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [resetTimer, timeoutMs, clearTimers]);

  return { resetTimer, clearTimers };
};

export default useIdleTimeout;
